import { ApplicantsFormDataValues } from './schema';
import { useMemo } from 'react';
import {
  useCustomer,
  useLoanApplication,
  usePerson,
} from '@axo/shared/data-access/hooks';

type DeepExhaustive<T> = {
  [K in keyof Required<T>]: T[K] extends object ? DeepExhaustive<T[K]> : T[K];
};

export const useApplicantsFormDefaultValues = (
  applicationID: string,
): DeepExhaustive<ApplicantsFormDataValues> => {
  const { data: applicationData } = useLoanApplication(applicationID);
  const { data: mainApplicantPersonData } = usePerson(
    applicationData?.PersonID,
  );
  const { data: mainApplicantCustomerData } = useCustomer(
    applicationData?.CustomerID,
  );
  const { data: coApplicantPersonData } = usePerson(
    applicationData?.CoApplicantPersonID,
  );
  const { data: coApplicantCustomerData } = useCustomer(
    applicationData?.CoApplicantCustomerID,
  );

  return useMemo(
    () => ({
      LoanApplication: {
        AccountClearingNumber:
          applicationData?.AccountClearingNumber ?? undefined,
        AccountNumber: applicationData?.AccountNumber ?? undefined,
        RegistrationNumber: applicationData?.RegistrationNumber ?? undefined,
      },
      MainApplicant: {
        Person: {
          SocialSecurityNumber:
            mainApplicantPersonData?.SocialSecurityNumber ?? '',
          FirstName: mainApplicantPersonData?.FirstName ?? undefined,
          LastName: mainApplicantPersonData?.LastName ?? undefined,
          ZipCode: mainApplicantPersonData?.ZipCode ?? undefined,
          City: mainApplicantPersonData?.City ?? undefined,
          Street: mainApplicantPersonData?.Street ?? undefined,
          StreetNumber: mainApplicantPersonData?.StreetNumber ?? undefined,
          StreetLevel: mainApplicantPersonData?.StreetLevel ?? undefined,
          StreetDoorSide: mainApplicantPersonData?.StreetDoorSide ?? undefined,
          AddressType: mainApplicantPersonData?.AddressType ?? undefined,
          Deceased: mainApplicantPersonData?.Deceased ?? undefined,
        },
        Customer: {
          Email: mainApplicantCustomerData?.Email ?? '',
          MobilePhone: {
            Number: mainApplicantCustomerData?.MobilePhoneNumber ?? undefined,
            CountryCode:
              mainApplicantCustomerData?.MobilePhoneCountryCode ?? undefined,
          },
        },
      },
      CoApplicant: {
        Person: {
          SocialSecurityNumber:
            coApplicantPersonData?.SocialSecurityNumber ?? '',
          FirstName: coApplicantPersonData?.FirstName ?? undefined,
          LastName: coApplicantPersonData?.LastName ?? undefined,
          ZipCode: coApplicantPersonData?.ZipCode ?? undefined,
          City: coApplicantPersonData?.City ?? undefined,
          Street: coApplicantPersonData?.Street ?? undefined,
          StreetNumber: coApplicantPersonData?.StreetNumber ?? undefined,
          StreetLevel: coApplicantPersonData?.StreetLevel ?? undefined,
          StreetDoorSide: coApplicantPersonData?.StreetDoorSide ?? undefined,
          AddressType: coApplicantPersonData?.AddressType ?? undefined,
          Deceased: coApplicantPersonData?.Deceased ?? undefined,
        },
        Customer: {
          Email: coApplicantCustomerData?.Email ?? '',
          MobilePhone: {
            Number: coApplicantCustomerData?.MobilePhoneNumber ?? undefined,
            CountryCode:
              coApplicantCustomerData?.MobilePhoneCountryCode ?? undefined,
          },
        },
      },
    }),
    [
      applicationData,
      mainApplicantPersonData,
      coApplicantPersonData,
      mainApplicantCustomerData,
      coApplicantCustomerData,
    ],
  );
};
